// Fixed-tier pricing configuration for TeamPulse
// Each tier has a fixed monthly price that includes up to a certain number of seats
// All tiers have the same features - only seat counts differ
const STANDARD_FEATURES = [
  "Weekly team reports",
  "Advanced analytics & trends",
  "Voice transcription",
  "Email support",
  "30-day free trial",
];

export const PRICING_TIERS = [
  {
    id: "starter",
    name: "Starter",
    minSeats: 1,
    maxSeats: 15,
    monthlyPrice: 30.0,
    description: "Perfect for small teams just getting started",
    features: ["Up to 15 team members", ...STANDARD_FEATURES],
  },
  {
    id: "growing",
    name: "Growing",
    minSeats: 16,
    maxSeats: 50,
    monthlyPrice: 75.0,
    description: "For growing teams building momentum",
    features: ["Up to 50 team members", ...STANDARD_FEATURES],
    popular: true,
  },
  {
    id: "team",
    name: "Team",
    minSeats: 51,
    maxSeats: 100,
    monthlyPrice: 100.0,
    description: "Built for established teams",
    features: ["Up to 100 team members", ...STANDARD_FEATURES],
  },
  {
    id: "business",
    name: "Business",
    minSeats: 101,
    maxSeats: 250,
    monthlyPrice: 175.0,
    description: "Designed for large organizations",
    features: ["Up to 250 team members", ...STANDARD_FEATURES],
  },
  {
    id: "scale",
    name: "Scale",
    minSeats: 251,
    maxSeats: 500,
    monthlyPrice: 225.0,
    description: "For organizations at scale",
    features: ["Up to 500 team members", ...STANDARD_FEATURES],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    minSeats: 501,
    maxSeats: null,
    monthlyPrice: null,
    description: "Custom solutions for unlimited teams",
    features: [
      "Unlimited team members",
      ...STANDARD_FEATURES,
      "Dedicated account manager",
      "Custom integrations",
    ],
    contactSales: true,
  },
] as const;

export type PricingTier = (typeof PRICING_TIERS)[number];
export type PricingTierId = PricingTier["id"];

/**
 * Get the pricing tier for a given seat count
 */
export function getPricingTierForSeatCount(seatCount: number): PricingTier {
  // Guard: seat counts below 1 default to starter tier
  if (seatCount < 1) {
    return PRICING_TIERS[0];
  }

  for (const tier of PRICING_TIERS) {
    if (tier.maxSeats === null) {
      // Enterprise tier - anything above the last defined max
      return tier;
    }
    if (seatCount >= tier.minSeats && seatCount <= tier.maxSeats) {
      return tier;
    }
  }
  // Default to first tier if somehow nothing matches
  return PRICING_TIERS[0];
}

/**
 * Calculate monthly price based on seat count
 * Returns the fixed monthly price for the tier the seat count falls into
 */
export function calculateMonthlyPrice(seatCount: number): number {
  const tier = getPricingTierForSeatCount(seatCount);

  // Enterprise tier requires sales contact
  if (tier.monthlyPrice === null) {
    return 0;
  }

  return tier.monthlyPrice;
}

/**
 * Get pricing breakdown for display
 */
export function getPricingBreakdown(seatCount: number) {
  const tier = getPricingTierForSeatCount(seatCount);
  const monthlyTotal = calculateMonthlyPrice(seatCount);

  return {
    tier: tier.name,
    tierId: tier.id,
    seatCount,
    maxSeats: tier.maxSeats,
    monthlyTotal,
    annualTotal: monthlyTotal * 12,
  };
}

export const TRIAL_DAYS = 30;
export const STRIPE_CURRENCY = "usd";
